import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { fetchStops, Stop, Journey, formatTime } from './analysisApi';

// ---- Palette ----
const C = {
  bg:             '#111e2d',
  bgRow:          'rgba(255,255,255,0.02)',
  bgRowHighlight: 'rgba(240,165,0,0.07)',
  bgRowCancelled: 'rgba(248,113,113,0.05)',
  border:         'rgba(255,255,255,0.06)',
  borderAccent:   'rgba(240,165,0,0.3)',
  text:           'rgba(255,255,255,0.85)',
  textMuted:      'rgba(255,255,255,0.35)',
  textDim:        'rgba(255,255,255,0.2)',
  amber:          '#f0a500',
  green:          '#4ade80',
  red:            '#f87171',
  headerBg:       '#0d1b2a',
};

// ---- Firestore event overlay ----
type StopEvent = {
  ab_received:      boolean;
  ab_time:          any;
  actual_departure: any;
  delay_ab_seconds: number | null;
  an_received:      boolean;
  an_time:          any;
  actual_arrival:   any;
  delay_an_seconds: number | null;
};
type StopEvents = Record<string, StopEvent>;

function toDate(v: any): Date | null {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (typeof v?.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function fmtFirestoreTime(v: any): string {
  const d = toDate(v);
  if (!d) return '—';
  return d.toLocaleTimeString('de-DE', {
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

// ---- Occupancy colours ----
const colorMap: Record<string, string> = {
  low:    '#6FAC5B',
  medium: '#FFB600',
  high:   '#FD0000',
};

// ---- Occupancy icons (original dashboard design) ----
function OccIcon({ state, color }: { state: string; color: string }) {
  if (state === 'low') return (
    <>
      <path d="M2.67203 10.3467H9.30072C10.7909 10.3467 11.9727 11.5151 11.9727 12.9883V20.1005H0V12.9883C0 11.5151 1.18186 10.3467 2.67203 10.3467Z" fill={color}/>
      <path d="M6.01145 8.21379C8.02637 8.21379 9.6598 6.59893 9.6598 4.6069C9.6598 2.61486 8.02637 1 6.01145 1C3.99652 1 2.3631 2.61486 2.3631 4.6069C2.3631 6.59893 3.99652 8.21379 6.01145 8.21379Z" fill={color}/>
      <path d="M17.1111 8.21379C19.126 8.21379 20.7595 6.59893 20.7595 4.6069C20.7595 2.61486 19.126 1 17.1111 1C15.0962 1 13.4628 2.61486 13.4628 4.6069C13.4628 6.59893 15.0962 8.21379 17.1111 8.21379Z" stroke={color} strokeMiterlimit="10"/>
      <path d="M22.2496 20.1005H21.1191V13.3948C21.1191 12.3279 20.1942 11.4135 19.0123 11.4135H13.7711V10.3467H19.0123C20.8108 10.3467 22.2496 11.7183 22.2496 13.3948V20.1005Z" fill={color}/>
      <path d="M27.337 8.21379C29.3519 8.21379 30.9854 6.59893 30.9854 4.6069C30.9854 2.61486 29.3519 1 27.337 1C25.3221 1 23.6887 2.61486 23.6887 4.6069C23.6887 6.59893 25.3221 8.21379 27.337 8.21379Z" stroke={color} strokeMiterlimit="10"/>
      <path d="M32.1672 20.1005H31.0367V13.3948C31.0367 12.3279 30.1118 11.4135 28.93 11.4135H23.6887V10.3467H28.93C30.7284 10.3467 32.1672 11.7183 32.1672 13.3948V20.1005Z" fill={color}/>
    </>
  );
  if (state === 'medium') return (
    <>
      <path d="M2.67206 9.34619H9.30084C10.791 9.34619 11.9729 10.5146 11.9729 11.9879V19.1002H0V11.9879C0 10.5146 1.18187 9.34619 2.67206 9.34619Z" fill={color}/>
      <path d="M6.01174 7.21388C8.02669 7.21388 9.66013 5.599 9.66013 3.60694C9.66013 1.61488 8.02669 0 6.01174 0C3.99678 0 2.36334 1.61488 2.36334 3.60694C2.36334 5.599 3.99678 7.21388 6.01174 7.21388Z" fill={color}/>
      <path d="M27.3374 7.21388C29.3524 7.21388 30.9858 5.599 30.9858 3.60694C30.9858 1.61488 29.3524 0 27.3374 0C25.3225 0 23.689 1.61488 23.689 3.60694C23.689 5.599 25.3225 7.21388 27.3374 7.21388Z" stroke={color}/>
      <path d="M32.1677 19.1002H31.0372V12.3943C31.0372 11.3275 30.1123 10.413 28.9304 10.413H23.689V9.34619H28.9304C30.7289 9.34619 32.1677 10.7178 32.1677 12.3943V19.1002Z" fill={color}/>
      <path d="M17.4346 7.21388C19.4495 7.21388 21.083 5.599 21.083 3.60694C21.083 1.61488 19.4495 0 17.4346 0C15.4196 0 13.7862 1.61488 13.7862 3.60694C13.7862 5.599 15.4196 7.21388 17.4346 7.21388Z" fill={color}/>
      <path d="M13.7862 19.0487L22.211 19.0996V11.9179C22.211 10.4408 20.5475 9.16748 19.0621 9.16748H13.7862V19.0487Z" fill={color}/>
    </>
  );
  return (
    <>
      <path d="M2.57098 9.36768H8.949C10.3828 9.36768 11.52 10.5387 11.52 12.0152V19.143H0V12.0152C0 10.5387 1.13717 9.36768 2.57098 9.36768Z" fill={color}/>
      <path d="M5.78492 7.22966C7.72365 7.22966 9.2953 5.61125 9.2953 3.61483C9.2953 1.61841 7.72365 0 5.78492 0C3.84619 0 2.27454 1.61841 2.27454 3.61483C2.27454 5.61125 3.84619 7.22966 5.78492 7.22966Z" fill={color}/>
      <path d="M16.3157 7.22966C18.2545 7.22966 19.8261 5.61125 19.8261 3.61483C19.8261 1.61841 18.2545 0 16.3157 0C14.377 0 12.8054 1.61841 12.8054 3.61483C12.8054 5.61125 14.377 7.22966 16.3157 7.22966Z" fill={color}/>
      <path d="M13.646 19.143H21.2601V12.0152C21.2601 10.5387 20.1229 9.36768 18.6891 9.36768H13.646V19.143Z" fill={color}/>
      <path d="M26.303 7.22966C28.2418 7.22966 29.8134 5.61125 29.8134 3.61483C29.8134 1.61841 28.2418 0 26.303 0C24.3643 0 22.7927 1.61841 22.7927 3.61483C22.7927 5.61125 24.3643 7.22966 26.303 7.22966Z" fill={color}/>
      <path d="M23.386 19.143H31V12.0152C31 10.5387 29.8629 9.36768 28.4291 9.36768H23.386V19.143Z" fill={color}/>
    </>
  );
}

// ---- Single 4-carriage unit (original dashboard SVG) ----
const CarriageUnit: React.FC<{ cars: string[]; confirmed: boolean; uic?: string }> = ({ cars, confirmed, uic }) => {
  const white     = '#ffffff';
  const capFill   = confirmed ? '#3a3a3a' : '#666666';
  const capStroke = confirmed ? '#555555' : '#777777';
  const carStroke = confirmed ? '#231F20' : 'rgba(150,150,150,0.4)';
  const sw        = '0.943742';

  const sectors = [
    { d: 'M58.6656 1.0874H12.3643V23.7897H58.6656V1.0874Z', tx: 25, ty: 6 },
    { d: 'M105.752 1.0874H58.6656V23.7897H105.752V1.0874Z', tx: 72, ty: 6 },
    { d: 'M152.053 1.0874H105.752V23.7897H152.053V1.0874Z', tx: 119, ty: 6 },
    { d: 'M199.139 1.0874H152.053V23.7897H199.139V1.0874Z', tx: 166, ty: 6 },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <svg width="214" height="25" viewBox="0 0 214 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M27.262 17.6367C27.262 19.6104 25.8783 23.7897 24.1757 23.7897H4.02849C0.614587 23.7897 0.149056 21.1647 0.903389 17.6367C1.65772 14.1088 7.16651 4.64495 7.16651 4.64495C8.26137 2.75021 10.6709 1.0874 12.3736 1.0874H24.1886C25.0091 1.09392 25.7941 1.47159 26.3723 2.13806C26.9504 2.80453 27.2749 3.70568 27.2749 4.64495L27.262 17.6367Z"
          fill={capFill} stroke={capStroke} strokeWidth={sw}/>
        <path d="M185.811 17.6367C185.811 19.6104 187.236 23.7897 188.988 23.7897H209.728C213.242 23.7897 213.722 21.1647 212.945 17.6367C212.169 14.1088 206.498 4.64495 206.498 4.64495C205.371 2.75021 202.89 1.0874 201.138 1.0874H188.975C188.13 1.09392 187.322 1.47159 186.727 2.13806C186.132 2.80453 185.798 3.70568 185.798 4.64495L185.811 17.6367Z"
          fill={capFill} stroke={capStroke} strokeWidth={sw}/>
        <path d="M58.9417 1.0874H12.4792V21.5668H58.9417V1.0874Z" stroke={carStroke} strokeWidth={sw}/>
        <path d="M105.403 1.0874H58.9407V21.5668H105.403V1.0874Z" stroke={carStroke} strokeWidth={sw}/>
        <path d="M151.866 1.0874H105.404V21.5668H151.866V1.0874Z" stroke={carStroke} strokeWidth={sw}/>
        <path d="M199.409 1.0874H151.866V21.5668H199.409V1.0874Z" stroke={carStroke} strokeWidth={sw}/>
        {sectors.map(({ d, tx, ty }, i) => {
          const occ     = cars[i] || 'low';
          const fillCol = confirmed ? colorMap[occ] : 'rgba(150,150,150,0.12)';
          return (
            <g key={i}>
              <path d={d} fill={fillCol} stroke={carStroke} strokeWidth={sw}/>
              {confirmed
                ? <g transform={`translate(${tx},${ty}) scale(0.645)`}><OccIcon state={occ} color={white}/></g>
                : <text x={tx + 12} y={15} fontSize="9" fontFamily="monospace" fill="rgba(150,150,150,0.5)" textAnchor="middle">?</text>
              }
            </g>
          );
        })}
      </svg>
      {uic && (
        <Typography sx={{ fontSize: '9px', color: C.textDim, fontFamily: '"IBM Plex Mono"', mt: '2px', letterSpacing: '0.02em' }}>
          {uic}
        </Typography>
      )}
    </Box>
  );
};

// ---- Formation: 1–3 coupled units ----
const DEFAULT_CARS = ['low', 'low', 'low', 'low'];

const Formation: React.FC<{ confirmed: boolean; numUnits?: number; cars?: string[][]; uics?: string[] }> = ({
  confirmed, numUnits = 2, cars, uics,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
    {Array.from({ length: numUnits }).map((_, i) => (
      <React.Fragment key={i}>
        <CarriageUnit cars={cars?.[i] || DEFAULT_CARS} confirmed={confirmed} uic={uics?.[i]} />
        {i < numUnits - 1 && (
          <Box sx={{ width: '6px', height: '8px', backgroundColor: confirmed ? '#231F20' : 'rgba(150,150,150,0.35)', borderRadius: '1px', flexShrink: 0 }} />
        )}
      </React.Fragment>
    ))}
  </Box>
);

// ---- Event indicator ----
const EventIndicator: React.FC<{ received: boolean; loading: boolean; active?: boolean }> = ({ received, loading, active = false }) => {
  if (loading) return <Box sx={{ width: 22, height: 22 }} />;
  return (
    <Box sx={{
      width: active ? 24 : 22, height: active ? 24 : 22, borderRadius: '4px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: received ? (active ? 'rgba(74,222,128,0.25)' : 'rgba(74,222,128,0.12)') : (active ? 'rgba(248,113,113,0.25)' : 'rgba(248,113,113,0.1)'),
      border: `${active ? '2px' : '1px'} solid ${received ? (active ? 'rgba(74,222,128,0.7)' : 'rgba(74,222,128,0.3)') : (active ? 'rgba(248,113,113,0.7)' : 'rgba(248,113,113,0.25)')}`,
      fontSize: active ? '13px' : '12px', fontWeight: active ? 700 : 400, flexShrink: 0,
    }}>
      {received ? '✓' : '✗'}
    </Box>
  );
};

// ---- Delay chip ----
const DelayChip: React.FC<{ seconds: number | null }> = ({ seconds }) => {
  if (seconds === null || seconds === undefined) return null;
  const mins = Math.round(seconds / 60);
  const onTime = Math.abs(mins) <= 1;
  return (
    <Box sx={{
      px: '5px', py: '1px', borderRadius: '3px', fontSize: '10px', fontFamily: '"IBM Plex Mono"',
      color: onTime ? C.green : (mins > 0 ? C.red : C.green),
      backgroundColor: onTime ? 'rgba(74,222,128,0.08)' : (mins > 0 ? 'rgba(248,113,113,0.1)' : 'rgba(74,222,128,0.08)'),
    }}>
      {onTime ? '±0' : `${mins > 0 ? '+' : ''}${mins}m`}
    </Box>
  );
};

// ---- Column header ----
const ColHeader: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' | 'center'; flex?: number | string }> = ({
  children, align = 'left', flex = 1,
}) => (
  <Typography sx={{ flex, fontSize: '11px', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', fontFamily: '"IBM Plex Mono"', textAlign: align, fontWeight: 500 }}>
    {children}
  </Typography>
);

// ---- Main component ----
const JourneyView: React.FC<{ journey: Journey }> = ({ journey }) => {
  const [stops,      setStops]      = useState<Stop[]>([]);
  const [stopEvents, setStopEvents] = useState<StopEvents>({});
  const [loading,    setLoading]    = useState(true);
  const [fsLoading,  setFsLoading]  = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); setError(null); setStops([]);
    fetchStops(journey.journey_id)
      .then(r => setStops(r.stops))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [journey.journey_id]);

  useEffect(() => {
    if (!journey.journey_id || !journey.service_day) return;
    setFsLoading(true); setStopEvents({});
    const ref = collection(db, 'journeys', journey.service_day, 'journeys', journey.journey_id, 'stops');
    const unsub = onSnapshot(ref,
      snap => { const ev: StopEvents = {}; snap.docs.forEach(d => { ev[d.id] = d.data() as StopEvent; }); setStopEvents(ev); setFsLoading(false); },
      err  => { console.error('❌ onSnapshot error', err); setFsLoading(false); }
    );
    return () => unsub();
  }, [journey.journey_id, journey.service_day]);

  const lastAbSeq = React.useMemo(() => {
    let last = -1;
    Object.entries(stopEvents).forEach(([seq, ev]) => { if (ev.ab_received && parseInt(seq) > last) last = parseInt(seq); });
    return last;
  }, [stopEvents]);

  if (loading) return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
      <CircularProgress sx={{ color: C.amber }} />
    </Box>
  );

  if (error) return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: C.bg }}>
      <Typography sx={{ color: C.red, fontFamily: '"IBM Plex Sans"', fontSize: '13px' }}>{error}</Typography>
    </Box>
  );

  return (
    <Box sx={{ flex: 1, backgroundColor: C.bg, overflowY: 'auto', fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      {/* Journey header */}
      <Box sx={{ backgroundColor: C.headerBg, borderBottom: `1px solid ${C.borderAccent}`, px: 4, py: 2, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 3, mb: '4px' }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '20px', fontWeight: 500, color: '#ffffff' }}>{journey.route_id}</Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: C.amber, letterSpacing: '0.1em' }}>{journey.line_id}</Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '13px', color: C.textMuted, flex: 1 }}>
            {formatTime(journey.scheduled_departure)} → {formatTime(journey.scheduled_arrival)} (Germany)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {[
              { val: Object.values(stopEvents).filter(e => e.ab_received).length, label: 'AB RX', color: C.green },
              { val: stops.length, label: 'STOPS', color: C.textMuted },
              { val: stops.length - Object.values(stopEvents).filter(e => e.ab_received).length, label: 'MISSING',
                color: stops.length > 0 && Object.values(stopEvents).filter(e => e.ab_received).length < stops.length ? C.red : C.green },
            ].map(({ val, label, color }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <Box sx={{ width: '1px', height: 28, backgroundColor: C.border }} />}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '16px', color, lineHeight: 1 }}>{val}</Typography>
                  <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '9px', color: C.textDim, letterSpacing: '0.1em' }}>{label}</Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.textDim, letterSpacing: '0.04em', userSelect: 'all' }}>
          {journey.journey_id}
        </Typography>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 4, py: '8px', borderBottom: `1px solid ${C.border}`, backgroundColor: 'rgba(0,0,0,0.15)' }}>
        <Typography sx={{ fontSize: '10px', color: C.textDim, fontFamily: '"IBM Plex Mono"', letterSpacing: '0.1em', textTransform: 'uppercase', mr: 1 }}>Key:</Typography>
        {[
          { bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.3)', symbol: '✓', label: 'Event received' },
          { bg: 'rgba(248,113,113,0.1)', border: 'rgba(248,113,113,0.25)', symbol: '✗', label: 'Event not received' },
        ].map(({ bg, border, symbol, label }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: bg, border: `1px solid ${border}`, fontSize: '12px', flexShrink: 0 }}>{symbol}</Box>
            <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Sans"' }}>{label}</Typography>
          </Box>
        ))}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', ml: 2 }}>
          <Box sx={{ width: 40, height: 14, backgroundColor: 'rgba(150,150,150,0.12)', border: '0.5px solid rgba(150,150,150,0.3)', borderRadius: '2px' }} />
          <Typography sx={{ fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Sans"' }}>Formation unconfirmed</Typography>
        </Box>
      </Box>

      {/* Column headers */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 4, py: '8px', borderBottom: `1px solid ${C.border}`, gap: 2 }}>
        <ColHeader flex="0 0 28px"> </ColHeader>
        <ColHeader flex={3}>Station</ColHeader>
        <ColHeader flex="0 0 22px" align="center">Ab.</ColHeader>
        <ColHeader flex="0 0 22px" align="center">An.</ColHeader>
        <ColHeader flex={1} align="right">Soll An.</ColHeader>
        <ColHeader flex={1} align="right">Ist An.</ColHeader>
        <ColHeader flex={1} align="right">Soll Ab.</ColHeader>
        <ColHeader flex={1} align="right">Ist Ab.</ColHeader>
        <ColHeader flex="0 0 48px" align="center">Vsp.</ColHeader>
        <ColHeader flex="0 0 32px" align="center">Gleis</ColHeader>
      </Box>

      {/* Stop rows */}
      {stops.map((stop, idx) => {
        const seqKey      = String(stop.stop_sequence);
        const event       = stopEvents[seqKey];
        const abRx        = event?.ab_received ?? false;
        const anRx        = event?.an_received ?? false;
        const isCurrent   = stop.stop_sequence === lastAbSeq;
        const isCancelled = stop.cancelled;
        const isFirst     = idx === 0;
        const isLast      = idx === stops.length - 1;

        const actualDepTime = event?.actual_departure ? fmtFirestoreTime(event.actual_departure) : '—';
        const actualArrTime = event?.actual_arrival   ? fmtFirestoreTime(event.actual_arrival)   : '—';

        // Formation confirmed if Ab. received — occupancy will be wired from Firestore later
        const formationConfirmed = abRx;

        return (
          <Box
            key={stop.stop_sequence}
            sx={{
              borderBottom: `1px solid ${C.border}`,
              borderLeft: isCurrent ? `3px solid ${C.amber}` : '3px solid transparent',
              backgroundColor: isCancelled ? C.bgRowCancelled : isCurrent ? C.bgRowHighlight : C.bgRow,
            }}
          >
            {/* Stop data row */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 4, py: '10px', gap: 2 }}>
              <Typography sx={{ flex: '0 0 28px', fontSize: '11px', color: C.textDim, fontFamily: '"IBM Plex Mono"', textAlign: 'right' }}>
                {stop.stop_sequence}
              </Typography>
              <Typography sx={{
                flex: 3, fontSize: '13px', fontFamily: '"IBM Plex Sans"',
                color: isCancelled ? C.red : isCurrent ? '#ffffff' : C.text,
                fontWeight: isCurrent ? 500 : 400,
                textDecoration: isCancelled ? 'line-through' : 'none',
              }}>
                {stop.stop_name}
                {isCancelled && (
                  <Typography component="span" sx={{ fontSize: '10px', ml: 1, color: C.red, fontFamily: '"IBM Plex Mono"' }}>ENTFÄLLT</Typography>
                )}
              </Typography>

              <Box sx={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}>
                {isFirst || isLast
                  ? <Typography sx={{ fontSize: '12px', color: C.textDim, fontFamily: '"IBM Plex Mono"' }}>—</Typography>
                  : <EventIndicator received={abRx} loading={fsLoading && !event} active={abRx} />}
              </Box>
              <Box sx={{ flex: '0 0 22px', display: 'flex', justifyContent: 'center' }}>
                {isFirst
                  ? <Typography sx={{ fontSize: '12px', color: C.textDim, fontFamily: '"IBM Plex Mono"' }}>—</Typography>
                  : <EventIndicator received={anRx} loading={fsLoading && !event} active={anRx} />}
              </Box>

              <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: (abRx || anRx) ? C.text : C.textMuted }}>
                {isFirst ? '—' : formatTime(stop.scheduled_arrival)}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: anRx ? C.text : C.textDim }}>
                {isFirst ? '—' : actualArrTime}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: abRx ? C.text : C.textMuted }}>
                {isLast ? '—' : formatTime(stop.scheduled_departure)}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: '12px', fontFamily: '"IBM Plex Mono"', textAlign: 'right', color: abRx ? C.text : C.textDim }}>
                {isLast ? '—' : actualDepTime}
              </Typography>
              <Box sx={{ flex: '0 0 48px', display: 'flex', justifyContent: 'center' }}>
                {isLast
                  ? (anRx && <DelayChip seconds={event?.delay_an_seconds ?? null} />)
                  : (abRx && <DelayChip seconds={event?.delay_ab_seconds ?? null} />)}
              </Box>
              <Typography sx={{ flex: '0 0 32px', fontSize: '11px', color: C.textMuted, fontFamily: '"IBM Plex Mono"', textAlign: 'center' }}>
                {stop.platform ?? '—'}
              </Typography>
            </Box>

            {/* Formation row — always visible below stop data */}
            <Box sx={{ px: 4, pb: '10px', display: 'flex', alignItems: 'center', gap: 1, backgroundColor: 'rgba(255,255,255,0.03)' }}>
              <Box sx={{ flex: '0 0 28px' }} />
              <Formation confirmed={formationConfirmed} numUnits={2} />
            </Box>

          </Box>
        );
      })}

      {stops.length === 0 && !loading && (
        <Box sx={{ px: 4, pt: 4 }}>
          <Typography sx={{ color: C.textMuted, fontFamily: '"IBM Plex Sans"', fontSize: '13px' }}>Keine Halte gefunden.</Typography>
        </Box>
      )}
    </Box>
  );
};

export default JourneyView;